import React, { useEffect, useMemo, useRef, useState } from "react";
import { Building2, LogOut, X, Search } from "lucide-react";

/**
 * Painel dropdown da unidade (porte do erpFront UnitPanel.tsx), adaptado ao
 * modelo de unidade do Fatura: unidades = [{ idUnidOPeVinculo, unidOpe }] e a
 * ativa é identificada por nome (`activeUnitName`, vindo de unidselpost).
 * A lista de seleção é inline (a SelectionList do erpFront não vem na lib).
 */
function unitInitials(name) {
  if (!name?.trim()) return "UN";
  return name.trim().substring(0, 2).toUpperCase();
}

export default function UnitPanel({
  open,
  unidades,
  loading = false,
  activeUnitName,
  onClose,
  onSelectUnidade,
  onLogout,
}) {
  const panelRef = useRef(null);
  const [busca, setBusca] = useState("");
  const [switchingId, setSwitchingId] = useState(null);

  useEffect(() => {
    if (!open) setSwitchingId(null);
  }, [open]);

  const lista = useMemo(() => unidades ?? [], [unidades]);
  const termo = busca.trim().toLowerCase();
  const filtradas = termo
    ? lista.filter((u) => (u.unidOpe || "").toLowerCase().includes(termo))
    : lista;

  useEffect(() => {
    if (!open) return undefined;
    function handleClick(e) {
      const t = e.target;
      if (panelRef.current && !panelRef.current.contains(t)) {
        const unitBtn = t.closest?.(".app-topbar__unit, .app-sidebar__footer-unit");
        if (!unitBtn) onClose();
      }
    }
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const initials = unitInitials(activeUnitName);
  const unitLabel = activeUnitName || "Nenhuma unidade selecionada";

  return (
    <section
      ref={panelRef}
      id="app-unit-panel"
      className="app-unit-panel"
      role="dialog"
      aria-label="Painel de unidade"
    >
      <div className="app-unit-panel__banner" role="status">
        <div className="app-unit-panel__banner-top">
          <div className="app-unit-panel__avatar" aria-hidden>
            {initials}
          </div>
          <div className="app-unit-panel__banner-session">
            <span className="app-unit-panel__session-user">{unitLabel}</span>
            <span className="app-unit-panel__session-bank">
              <em>Unidade ativa</em>
            </span>
          </div>
          <button
            type="button"
            className="app-unit-panel__close"
            onClick={onClose}
            aria-label="Fechar painel"
            title="Fechar"
          >
            <X size={16} strokeWidth={2.2} />
          </button>
        </div>

        <div className="app-unit-panel__banner-divider" aria-hidden />

        <div className="app-unit-panel__banner-unit-row">
          <span className="app-unit-panel__banner-icon" aria-hidden>
            <Building2 size={16} strokeWidth={2.2} />
          </span>
          <span className="app-unit-panel__banner-copy">
            <span className="app-unit-panel__banner-label">Unidade ativa</span>
            <span className="app-unit-panel__banner-unit" title={unitLabel}>
              {unitLabel}
            </span>
          </span>
          <span className="app-unit-panel__banner-badge">ATIVA</span>
        </div>
      </div>

      <div className="fatura-unit-list-wrap">
        <div className="fatura-unit-list-head">
          <span>Trocar unidade</span>
          <span className="fatura-unit-list-count">{lista.length}</span>
        </div>
        <div className="fatura-unit-search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Procurar unidade…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="fatura-unit-list">
          {loading && <div className="fatura-unit-empty">Carregando unidades…</div>}
          {!loading && filtradas.length === 0 && (
            <div className="fatura-unit-empty">Nenhuma unidade encontrada</div>
          )}
          {!loading &&
            filtradas.map((u) => {
              const isAtiva = u.unidOpe === activeUnitName;
              const switching = switchingId === u.idUnidOPeVinculo;
              return (
                <button
                  type="button"
                  key={u.idUnidOPeVinculo}
                  className={`fatura-unit-row${isAtiva ? " is-active" : ""}${
                    switching ? " is-switching" : ""
                  }`}
                  disabled={!!switchingId}
                  onClick={() => {
                    if (isAtiva || switchingId) return;
                    setSwitchingId(u.idUnidOPeVinculo);
                    onSelectUnidade(u.idUnidOPeVinculo);
                  }}
                >
                  <span className="fatura-unit-row__icon">
                    <Building2 size={16} />
                  </span>
                  <span className="fatura-unit-row__name">{u.unidOpe}</span>
                  {switching ? (
                    <span className="fatura-unit-row__spin" aria-label="Trocando unidade" />
                  ) : isAtiva ? (
                    <span className="fatura-unit-row__badge">Ativa</span>
                  ) : null}
                </button>
              );
            })}
        </div>
      </div>

      <button type="button" className="app-unit-panel__logout" onClick={onLogout}>
        <LogOut size={17} strokeWidth={2} />
        Sair do sistema
      </button>
    </section>
  );
}
